import { useMemo } from "react";

import { type ReviewQuestionType } from "../../redesign/Workplace/Review/types";
import {
  makeRatingStepId,
  type NumberQuestionConfig,
  type PostShiftReviewStep,
  type QuestionMappings,
  QuestionTag,
} from "../types";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./useIsWorkplaceReviewUXUpdateEnabled";

const RENDERABLE_QUESTION_TAGS = new Set<QuestionTag>([
  QuestionTag.SOFTWARE,
  QuestionTag.SUPPLIES,
  QuestionTag.PATIENTS_PER_NURSE,
  QuestionTag.PARKING,
]);

interface StepConfig {
  type: PostShiftReviewStep;
  required: boolean;
  questionId?: string;
  options?: Array<{ id: string; displayText: string }>;
  questionText?: string;
  questionType?: ReviewQuestionType;
  numberConfig?: NumberQuestionConfig;
  ratingTag?: QuestionTag;
  ratingQuestionId?: string;
}

export interface UsePostShiftReviewStepsProps {
  questionMappings?: QuestionMappings;
  isError?: boolean;
}

const getQuestionTextForTag = (tag: QuestionTag): string => {
  // FIXME: Automatically disabled during ESLint config update
  switch (tag) {
    case QuestionTag.SOFTWARE: {
      return "What software was used for documentation?";
    }

    case QuestionTag.SUPPLIES: {
      return "Did you have sufficient supplies during the shift?";
    }

    case QuestionTag.PATIENTS_PER_NURSE: {
      return "How many residents were you responsible for during this shift?";
    }

    case QuestionTag.PARKING: {
      return "Tell us about parking at this location";
    }

    default: {
      return "";
    }
  }
};

export function usePostShiftReviewSteps(props: UsePostShiftReviewStepsProps) {
  const { questionMappings, isError } = props;
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  return useMemo(() => {
    const questionSteps: StepConfig[] = [];
    let writeReviewStep: StepConfig | undefined;

    if (!isError && questionMappings) {
      if (questionMappings.commentQuestionId) {
        writeReviewStep = { type: "write-review", required: true };
      }

      const orderedTags = questionMappings.orderedTags ?? [
        QuestionTag.SOFTWARE,
        QuestionTag.SUPPLIES,
        QuestionTag.PATIENTS_PER_NURSE,
        QuestionTag.PARKING,
        QuestionTag.CLEANLINESS,
        QuestionTag.SUPPLY_RATING,
        QuestionTag.CULTURE,
        QuestionTag.PATIENT_CARE,
      ];

      for (const tag of orderedTags) {
        if (RENDERABLE_QUESTION_TAGS.has(tag)) {
          const questionId = questionMappings.tagToQuestionId[tag];
          if (questionId) {
            const options = questionMappings.questionOptions[questionId];
            const numberConfig = questionMappings.questionNumberConfig[questionId];
            const questionType = questionMappings.questionTypes[questionId];

            questionSteps.push({
              type: tag as PostShiftReviewStep,
              required: false,
              questionId,
              options,
              questionText: getQuestionTextForTag(tag),
              questionType,
              numberConfig,
            });
          }
        }

        // Add rating step for this tag if one exists (placed after the tag's question step)
        const ratingQuestion = questionMappings.ratingQuestions.find((rq) => rq.tag === tag);
        if (ratingQuestion) {
          questionSteps.push({
            type: makeRatingStepId(tag),
            required: false,
            questionText: ratingQuestion.questionText,
            ratingTag: ratingQuestion.tag,
            ratingQuestionId: ratingQuestion.questionId,
          });
        }
      }
    }

    if (isWriteReviewUiUpdateEnabled) {
      // New flow: intro → [tag questions] → pick-rating → write-review → choose-anonymity → end
      const baseSteps: StepConfig[] = [{ type: "intro", required: true }];
      const contentSteps: StepConfig[] = [
        ...questionSteps,
        { type: "pick-rating", required: true },
      ];
      if (writeReviewStep) {
        contentSteps.push(writeReviewStep, { type: "choose-anonymity", required: true });
      }
      const allSteps: StepConfig[] = [
        ...baseSteps,
        ...contentSteps,
        { type: "end", required: true },
      ];

      return {
        steps: allSteps,
        stepTypes: allSteps.map((step) => step.type),
        getStepConfig: (stepType: PostShiftReviewStep) =>
          allSteps.find((step) => step.type === stepType),
      };
    }

    // Old flow: pick-rating → [write-review + tag questions] → end
    const baseSteps: StepConfig[] = [{ type: "pick-rating", required: true }];
    const contentSteps: StepConfig[] = [];
    if (writeReviewStep) {
      contentSteps.push(writeReviewStep);
    }
    contentSteps.push(...questionSteps);
    const allSteps: StepConfig[] = [...baseSteps, ...contentSteps, { type: "end", required: true }];

    return {
      steps: allSteps,
      stepTypes: allSteps.map((step) => step.type),
      getStepConfig: (stepType: PostShiftReviewStep) =>
        allSteps.find((step) => step.type === stepType),
    };
  }, [questionMappings, isError, isWriteReviewUiUpdateEnabled]);
}
